import { useObserva } from "@morganisbatman/observa-client-react";
import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { authClient } from "../src/auth-client";
import { BrandSplit } from "../src/brand-split";
import { Button } from "../src/components/button";
import { SocialButtons } from "../src/social-buttons";
import { type Colors, useColors } from "../src/theme";

export default function Login(): JSX.Element {
  const router = useRouter();
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // True when sign-in was blocked because the email isn't verified yet —
  // BetterAuth rejects with a 403 EMAIL_NOT_VERIFIED. We surface an
  // actionable "resend verification" affordance in that case.
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">(
    "idle",
  );

  async function handleResendVerification(): Promise<void> {
    setResendState("sending");
    setError(null);
    try {
      // Unauthenticated BetterAuth endpoint — re-issues the verification
      // link for an as-yet-unverified address (no session required).
      const res = await authClient.sendVerificationEmail({
        email,
        callbackURL: "/",
      });
      if (res.error) {
        setError(res.error.message ?? "couldn't resend verification email");
        setResendState("idle");
        return;
      }
      setResendState("sent");
      setTimeout(() => setResendState("idle"), 30_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "couldn't resend");
      setResendState("idle");
    }
  }

  async function handlePassword(): Promise<void> {
    setBusy(true);
    setError(null);
    setNeedsVerify(false);
    void observa.event({
      eventType: "login.submitted",
      payload: { method: "password" },
    });
    try {
      const res = await authClient.signIn.email({ email, password });
      if (res.error) {
        const reason = res.error.message ?? "login failed";
        setError(reason);
        // BetterAuth blocks unverified users (status 403, code
        // EMAIL_NOT_VERIFIED). Detect it from the code/status/message so
        // we can offer to resend the link instead of dead-ending.
        const code = (res.error as { code?: string }).code ?? "";
        const isUnverified =
          res.error.status === 403 ||
          code.toUpperCase().includes("EMAIL_NOT_VERIFIED") ||
          /verif/i.test(reason);
        if (isUnverified) setNeedsVerify(true);
        void observa.event({
          eventType: "login.failed",
          payload: { method: "password", reason },
        });
        return;
      }
      void observa.event({
        eventType: "login.password_succeeded",
        payload: {},
      });
      router.replace("/");
    } catch (err) {
      const reason = err instanceof Error ? err.message : "login failed";
      setError(reason);
      void observa.event({
        eventType: "login.failed",
        payload: { method: "password", reason },
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleMagicLink(): Promise<void> {
    setBusy(true);
    setError(null);
    void observa.event({
      eventType: "login.submitted",
      payload: { method: "magic_link" },
    });
    try {
      const res = await authClient.signIn.magicLink({
        email,
        callbackURL: "/",
      });
      if (res.error) {
        const reason = res.error.message ?? "magic link failed";
        setError(reason);
        void observa.event({
          eventType: "login.failed",
          payload: { method: "magic_link", reason },
        });
        return;
      }
      setMagicLinkSent(true);
      void observa.event({
        eventType: "login.magic_link_sent",
        payload: {},
      });
    } catch (err) {
      const reason = err instanceof Error ? err.message : "login failed";
      setError(reason);
      void observa.event({
        eventType: "login.failed",
        payload: { method: "magic_link", reason },
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <BrandSplit
      panelTitle="Welcome back."
      panelBody="Pick your prep up exactly where you left it — your readiness, your weak topics, your place in the pool."
    >
      {(isWide) => (
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={{ flex: 1 }}
        >
          <View style={styles.form}>
          {!isWide ? <Text style={styles.title}>Log in</Text> : null}
          <TextInput
          style={styles.input}
          placeholder="Email"
          placeholderTextColor={colors.textMuted}
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          autoComplete="email"
          accessibilityLabel="Email address"
        />
        <TextInput
          style={styles.input}
          placeholder="Password"
          placeholderTextColor={colors.textMuted}
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          autoComplete="current-password"
          accessibilityLabel="Password"
        />
        {error ? (
          <Text style={styles.error} accessibilityLiveRegion="polite">
            {error}
          </Text>
        ) : null}
        {needsVerify ? (
          <Button
            label={
              resendState === "sending"
                ? "Sending…"
                : resendState === "sent"
                  ? "Verification email sent"
                  : "Resend verification email"
            }
            onPress={handleResendVerification}
            disabled={busy || !email || resendState !== "idle"}
            variant="secondary"
            fullWidth
            accessibilityLabel="Resend verification email"
          />
        ) : null}
        {magicLinkSent ? (
          <Text style={styles.notice} accessibilityLiveRegion="polite">
            Magic link sent. Check your email to finish signing in.
          </Text>
        ) : null}
        <Button
          label="Log in"
          onPress={handlePassword}
          disabled={!email || !password}
          busy={busy}
          fullWidth
          accessibilityLabel="Log in"
        />
        <View style={isWide ? styles.inlineRow : undefined}>
          <Button
            label="Send magic link instead"
            onPress={handleMagicLink}
            disabled={busy || !email || magicLinkSent}
            variant="secondary"
            fullWidth={!isWide}
            style={isWide ? styles.flex : undefined}
            accessibilityLabel="Send magic link instead"
          />
          <Pressable
            onPress={() => router.push("/forgot-password")}
            accessibilityRole="link"
            accessibilityLabel="Forgot your password"
            style={styles.linkBtn}
          >
            <Text style={styles.linkText}>Forgot your password?</Text>
          </Pressable>
        </View>
        <SocialButtons callbackURL="/" intent="login" row={isWide} />

        <Pressable
          onPress={() => router.push("/signup")}
          accessibilityRole="link"
          accessibilityLabel="Sign up for an account"
          style={styles.linkBtn}
        >
          <Text style={styles.linkText}>Need an account? Sign up</Text>
        </Pressable>
          </View>
        </KeyboardAvoidingView>
      )}
    </BrandSplit>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  title: { ...textStyle.h2, color: colors.heading, marginBottom: space[2] },
  form: { gap: space[3], width: "100%" },
  // Web/wide: magic-link button + forgot link on one row (§8).
  inlineRow: { flexDirection: "row", alignItems: "center", gap: space[3] },
  flex: { flex: 1 },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.md,
    paddingVertical: space[3],
    paddingHorizontal: space[4],
    fontSize: 16,
    fontFamily: fontFamily.regular,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
  },
  error: { ...textStyle.bodySmall, color: colors.error },
  notice: { ...textStyle.bodySmall, color: colors.success },
  linkBtn: { paddingVertical: space[2], alignItems: "center" },
  linkText: { ...textStyle.bodySmall, color: colors.textMuted },
  });
}
